import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";

const inp = "w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[12.5px] outline-none focus:border-[#ff4b2b]/60";

function EntityPage({ kind, endpoint, fields, title }) {
  const { locale } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});

  const load = () => api.get(endpoint).then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line

  const create = async () => {
    if (!f.name) return;
    try {
      await api.post(endpoint, f);
      setF({});
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const upload = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/assets/upload", fd);
      const item = list.find((x) => x.id === id);
      await api.patch(`${endpoint}/${id}`, {
        reference_asset_ids: [...(item.reference_asset_ids || []), data.id],
      });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid={`${kind}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">{kind} lock</p>
          <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">{title}</h1>
        </div>
        <button onClick={() => setOpen(!open)} data-testid={`new-${kind}-btn`} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold">
          <Plus className="h-4 w-4" /> {locale === "fr" ? "Créer" : "Create"}
        </button>
      </div>

      {open && (
        <div className="mt-8 grid max-w-[900px] gap-3 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6 sm:grid-cols-2" data-testid={`${kind}-form`}>
          {fields.map((k) => (
            <input key={k} className={inp} placeholder={k.replace("_", " ")} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${kind}-field-${k}`} />
          ))}
          <button onClick={create} data-testid={`${kind}-submit`} className="btn-primary rounded-lg py-2.5 text-[13px] font-semibold">
            {locale === "fr" ? "Enregistrer" : "Save"}
          </button>
        </div>
      )}

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {list.map((x) => (
          <div key={x.id} className="overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]" data-testid={`${kind}-card-${x.id}`}>
            <div className="flex h-32 gap-px bg-[#0e0e15]">
              {(x.reference_asset_ids || []).slice(0, 3).map((aid) => (
                <img key={aid} src={fileUrl(aid)} alt="" className="h-32 flex-1 object-cover" />
              ))}
              {(x.reference_asset_ids || []).length === 0 && (
                <div className="grid h-32 w-full place-items-center">
                  <p className="mono text-[10px] uppercase tracking-[0.2em] text-[#8288a0]">no reference</p>
                </div>
              )}
            </div>
            <div className="p-4">
              <p className="mono text-[10px] uppercase tracking-[0.14em] text-[#ff4b2b]">{x.code}</p>
              <p className="mt-1.5 font-display text-base font-medium">{x.name}</p>
              <p className="mt-1.5 line-clamp-2 text-[12px] text-[#9194a6]">{x.description || x.appearance || "—"}</p>
              <div className="mt-3 flex gap-2">
                <label className="btn-ghost inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full py-1.5 text-[10.5px] font-semibold">
                  <Upload className="h-3 w-3" /> {locale === "fr" ? "Référence" : "Reference"}
                  <input type="file" accept="image/*" className="hidden" data-testid={`${kind}-upload-${x.id}`} onChange={(e) => e.target.files?.[0] && upload(x.id, e.target.files[0])} />
                </label>
                <button onClick={async () => { await api.delete(`${endpoint}/${x.id}`); load(); }} data-testid={`${kind}-delete-${x.id}`} className="grid h-7 w-7 place-items-center rounded-full border border-[#2a2c3c] text-[#8288a0] hover:text-[#ff4b4b]">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-[13px] text-[#9194a6]">{locale === "fr" ? "Rien ici encore." : "Nothing here yet."}</p>}
      </div>
    </div>
  );
}

export function Characters() {
  const { locale } = useI18n();
  return (
    <EntityPage
      kind="character"
      endpoint="/characters"
      title={locale === "fr" ? "Personnages persistants" : "Persistent characters"}
      fields={["name", "age", "appearance", "clothing", "accessories", "personality", "voice", "description"]}
    />
  );
}

export function Locations() {
  const { locale } = useI18n();
  return (
    <EntityPage
      kind="location"
      endpoint="/locations"
      title={locale === "fr" ? "Décors persistants" : "Persistent locations"}
      fields={["name", "description", "architecture", "lighting", "time_of_day", "weather", "color"]}
    />
  );
}

export function Assets() {
  const { locale } = useI18n();
  const [list, setList] = useState([]);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (kind) p.set("kind", kind);
    if (q) p.set("q", q);
    api.get(`/assets?${p}`).then(({ data }) => setList(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [kind, q]); // eslint-disable-line

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/assets/upload", fd); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="assets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Media library</p>
          <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
            {locale === "fr" ? "Vos médias" : "Your assets"}
          </h1>
        </div>
        <label className="btn-amber inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold">
          <Upload className="h-4 w-4" /> {locale === "fr" ? "Importer" : "Upload"}
          <input type="file" className="hidden" data-testid="asset-upload" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        <input className={`${inp} w-48`} placeholder={locale === "fr" ? "Rechercher…" : "Search…"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="asset-search" />
        {[["", "All"], ["image", "Images"], ["video", "Videos"], ["audio", "Audio"]].map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`asset-filter-${k || "all"}`} className={`rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors ${kind === k ? "border-white bg-white text-black" : "border-[#2a2c3c] text-[#9194a6] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {list.map((a) => (
          <div key={a.id} className="overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]" data-testid={`asset-${a.id}`}>
            <div className="aspect-video bg-[#0e0e15]">
              {a.kind === "video" ? (
                <video src={fileUrl(a.id)} className="h-full w-full object-cover" muted />
              ) : a.kind === "image" ? (
                <img src={fileUrl(a.id)} alt={a.name} className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <div className="grid h-full place-items-center"><p className="mono text-[10px] text-[#8288a0]">AUDIO</p></div>
              )}
            </div>
            <div className="flex items-center justify-between p-3">
              <p className="truncate text-[11.5px]">{a.name}</p>
              <div className="flex gap-1.5">
                <button onClick={async () => { await api.patch(`/assets/${a.id}`, { favorite: !a.favorite }); load(); }} data-testid={`asset-fav-${a.id}`}>
                  <Star className={`h-3.5 w-3.5 ${a.favorite ? "fill-[#ff4b2b] text-[#ff4b2b]" : "text-[#8288a0]"}`} />
                </button>
                <button onClick={async () => { await api.delete(`/assets/${a.id}`); load(); }} data-testid={`asset-delete-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-[#8288a0] hover:text-[#ff4b4b]" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-[13px] text-[#9194a6]">—</p>}
      </div>
    </div>
  );
}

export function Storyboards() {
  const { locale } = useI18n();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/ai/storyboards").then(({ data }) => setList(data)).catch(() => {}); }, []);

  return (
    <div className="p-6 lg:p-10" data-testid="storyboards-page">
      <p className="overline">Storyboards</p>
      <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
        {locale === "fr" ? "Vos storyboards" : "Your storyboards"}
      </h1>
      <div className="mt-10 space-y-6">
        {list.map((b) => (
          <div key={b.id} className="rounded-2xl border border-[#212230] bg-[#0b0b11] p-6" data-testid={`storyboard-${b.id}`}>
            <p className="font-display text-lg font-medium">{b.title}</p>
            <p className="mt-2 text-[12.5px] text-[#9194a6]">{b.concept}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(b.scenes || []).map((s) => (
                <div key={s.order} className="rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4">
                  <p className="mono text-[10px] uppercase text-[#ff4b2b]">SC {String(s.order).padStart(2, "0")} · {s.duration}s</p>
                  <p className="mt-1.5 text-[12.5px] font-medium">{s.title}</p>
                  <p className="mt-1 line-clamp-3 text-[11.5px] text-[#9194a6]">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <p className="text-[13px] text-[#9194a6]">
            {locale === "fr" ? "Générez un storyboard depuis un projet." : "Generate a storyboard from a project."}
          </p>
        )}
      </div>
    </div>
  );
}
